import { Button, Dialog } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { isNil } from 'ramda';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { triggerEvent } from 'src/js/events';
import { constant } from 'src/js/constant';
import { useMutable } from 'src/js/hook/use-mutable';
import { createListStore, ListSelector } from '../../util/listSelector';
import { v4 as uuidv4 } from 'uuid';
import { PresetLoadTable } from './preset-load-table';
import { Presets, Preset } from './preset-models';
import { loadPreset } from './preset-store';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { RebootNeededNotSavedModal } from 'src/js/reboot/reboot-needed-not-saved-modal';
import { TwoButtonsModal } from 'src/js/modals/two-buttons-modal';

interface Props {
  presets: Presets;
  onCancel: VoidFunction;
  shouldReloadData: VoidFunction;
}

export const PresetLoad: React.FunctionComponent<Props> = ({ presets, onCancel, shouldReloadData }) => {
  const { t } = useTranslation();

  let listController: ListSelector<Preset> = null;
  const [uuid] = useMutable(uuidv4());
  listController = createListStore(presets.data, uuid(), 'name');

  const [selected, setSelected] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [savePromptFromLoadModalShown, showSavePromptFromLoadModal, hideSavePromptFromLoadModal] = useModalControls();
  const [reloadWarningModalShown, showReloadWarningModal, hideReloadWarningModal] = useModalControls(); 
  const [selectedFile, setSelectedFile] = useMutable(null);

  const cancelLoad = () => {
    hideSavePromptFromLoadModal();
    hideReloadWarningModal();
        setButtonState(undefined);
      };

  const handleLoad = () => {
    setButtonState('pending');
    const selectedFile = listController.selectedRows()?.[0];
    if (selectedFile == null){
       /* eslint-disable-next-line no-console */
       console.error('No preset selected to load');
       return null;
    }
    
    // PROMPT IF UNSAVED
    if (presets.activeWasModified && selectedFile.name !== presets.active) {
      setSelectedFile(selectedFile);
      showSavePromptFromLoadModal();
      return null;
    } else if (presets.activeWasModified && selectedFile.name === presets.active) {
      setSelectedFile(selectedFile);
      showReloadWarningModal();
      return null;
    } 

    return proceedLoad(selectedFile);
  };

  const proceedLoad = (selectedFile: Preset) => {
    return loadPreset(selectedFile, t, true).then((res) => {
      setButtonState(undefined);
      if (res.ok) {
        listController.unselectAll();
        onCancel();
        shouldReloadData();
        triggerEvent('preset-load');
      }
    });
  };

  if (isNil(presets)) {
    return null;
  }

  const content = (): ReactNode => {
    return (
      <form className="HaiForm">
        <PresetLoadTable listController={listController} presets={presets} setSelected={setSelected} />
        <div className="d-flex justify-content-end hai-mt-8">
          <Button onClick={onCancel}>{t('general.cancel')}</Button>
          <Button onClick={handleLoad} state={buttonState} variant="primary" disabled={!selected}>
            {t('general.load')}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <>
    <Dialog
      accentColor={constant.productColor}
      title={t('presets.presetLoad')}
      onClose={onCancel}
      show={true}
      dialogType="activity"
      headerIcon="Presets"
      content={content()}
      autoScroll={false}
      size="lg"
      className="mk-modal-not-so-large"
    />
     {savePromptFromLoadModalShown && (
              //TODO: Rename modal, find better name
              <RebootNeededNotSavedModal
                onCancel={cancelLoad}
                onReboot={() => {
                  //import
                  hideSavePromptFromLoadModal();
                  proceedLoad(selectedFile());
                }}
                onSave={() => {
                  //Save and import
                  hideSavePromptFromLoadModal();
                  triggerEvent('stop-preset-fetch');
                  triggerEvent('preset-save-callback', {
                    offerSetAsStartup: false,
                    callback: (_res: any) => {
                      proceedLoad(selectedFile()); // Import and load new preset file
                    },
                  });
                }}
                bodyText={
                  <>
                    {t('upload.presetLoadNotSavedModal.body1')}
                    <br />
                    {t('upload.presetLoadNotSavedModal.body2')}
                  </>
                }
                middleLabel={t('general.load')}
                lastLabel={t('upload.saveAndLoad')}
                middleVariant="secondary"
                lastVariant="primary"
              />
            )}
            
              {reloadWarningModalShown && (
                                  <TwoButtonsModal
                                    onCancel={cancelLoad}
                                    onPrimary={() => {
                                      //import
                                      hideReloadWarningModal();
                                      proceedLoad(selectedFile());
                                    }}
                                    title={t('presets.loadPresetConfirmTitle')}
                                    bodyText={
                                      <>
                                        {t('presets.loadPresetConfirmBody')}
                                      </>
                                    }
                                    primaryLabel={t('general.load')}
                                    primaryVariant="primary"
                                  />
                                )}
                                </>
  );
};
